var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { createDemoPeriodComparisonMatcher } from '@web/lib/demo/utils';
import { avgParticipantsFromRaw, emptyMeetingLoadRawCounts } from '@workalizer/shared';
import { diffComparisonValueFormat } from '@workalizer/shared/types';
import { getInterval, getPercentageDiff, getPreviousPeriod, groupByPeriod, randomInt } from '@workalizer/shared/utils';
import { endOfDay, startOfDay } from 'date-fns';
var generateMockRaw = function () {
    var meetingCount = randomInt(4, 14);
    var durationSeconds = randomInt(2, 12) * 3600 + randomInt(0, 59) * 60;
    var avgParticipants = randomInt(30, 80) / 10;
    var participantsSum = Math.round(meetingCount * avgParticipants);
    var manHoursSeconds = durationSeconds * Math.max(1, Math.round(avgParticipants));
    return {
        meetingCount: meetingCount,
        durationSeconds: durationSeconds,
        manHoursSeconds: manHoursSeconds,
        participantsSum: participantsSum
    };
};
var toMetrics = function (raw) { return ({
    meetingCount: raw.meetingCount,
    durationSeconds: raw.durationSeconds,
    manHoursSeconds: raw.manHoursSeconds,
    avgParticipants: avgParticipantsFromRaw(raw)
}); };
export var meetingLoadAnalytic = function (input) {
    var includeTeamMetrics = input.userId == null;
    var interval = getInterval(input.dateFrom, input.dateTo);
    var dateFrom = startOfDay(input.dateFrom);
    var dateTo = endOfDay(input.dateTo);
    var _a = getPreviousPeriod(input.dateFrom, input.dateTo), prevPeriodDateFrom = _a.prevPeriodDateFrom, prevPeriodDateTo = _a.prevPeriodDateTo;
    var periods = groupByPeriod([], dateFrom, dateTo, interval);
    var previousPeriods = groupByPeriod([], startOfDay(prevPeriodDateFrom), endOfDay(prevPeriodDateTo), interval);
    var periodMatcher = createDemoPeriodComparisonMatcher(input, interval, periods, previousPeriods);
    var periodsData = periods.map(function (period, idx) {
        var _a;
        var currentData = toMetrics(generateMockRaw());
        var prevPeriod = periodMatcher.getItem(idx, previousPeriods);
        var prevData = toMetrics(generateMockRaw());
        var teamData = input.userId
            ? toMetrics(generateMockRaw())
            : toMetrics(emptyMeetingLoadRawCounts());
        return __assign({ period: period.date, previousPeriodLabel: (_a = prevPeriod === null || prevPeriod === void 0 ? void 0 : prevPeriod.date) !== null && _a !== void 0 ? _a : '', meetingCount: currentData.meetingCount, durationSeconds: currentData.durationSeconds, prevMeetingCount: prevData.meetingCount, prevDurationSeconds: prevData.durationSeconds, teamMeetingCount: teamData.meetingCount, teamDurationSeconds: teamData.durationSeconds }, (includeTeamMetrics && {
            manHoursSeconds: currentData.manHoursSeconds,
            avgParticipants: currentData.avgParticipants,
            prevManHoursSeconds: prevData.manHoursSeconds,
            prevAvgParticipants: prevData.avgParticipants,
            teamManHoursSeconds: teamData.manHoursSeconds,
            teamAvgParticipants: teamData.avgParticipants
        }));
    });
    var currentTotals = periodsData.reduce(function (acc, period) {
        var _a, _b;
        acc.meetingCount += period.meetingCount;
        acc.durationSeconds += period.durationSeconds;
        if (includeTeamMetrics) {
            acc.manHoursSeconds += (_a = period.manHoursSeconds) !== null && _a !== void 0 ? _a : 0;
            acc.participantsSum += Math.round(period.meetingCount * ((_b = period.avgParticipants) !== null && _b !== void 0 ? _b : 0));
        }
        return acc;
    }, emptyMeetingLoadRawCounts());
    var prevTotals = periodsData.reduce(function (acc, period) {
        var _a, _b;
        acc.meetingCount += period.prevMeetingCount;
        acc.durationSeconds += period.prevDurationSeconds;
        if (includeTeamMetrics) {
            acc.manHoursSeconds += (_a = period.prevManHoursSeconds) !== null && _a !== void 0 ? _a : 0;
            acc.participantsSum += Math.round(period.prevMeetingCount * ((_b = period.prevAvgParticipants) !== null && _b !== void 0 ? _b : 0));
        }
        return acc;
    }, emptyMeetingLoadRawCounts());
    var currentMetrics = toMetrics(currentTotals);
    var previousMetrics = toMetrics(prevTotals);
    var teamMetrics = input.userId
        ? __assign({ meetingCount: periodsData.reduce(function (sum, period) { return sum + period.teamMeetingCount; }, 0), durationSeconds: periodsData.reduce(function (sum, period) { return sum + period.teamDurationSeconds; }, 0) }, (includeTeamMetrics && {
            manHoursSeconds: periodsData.reduce(function (sum, period) { var _a; return sum + ((_a = period.teamManHoursSeconds) !== null && _a !== void 0 ? _a : 0); }, 0),
            avgParticipants: periodsData.reduce(function (sum, period) { var _a; return sum + ((_a = period.teamAvgParticipants) !== null && _a !== void 0 ? _a : 0); }, 0) / Math.max(periodsData.length, 1)
        })) : undefined;
    var buildMetricTotal = function (key, previousValue, teamValue) { return (__assign({ current: currentMetrics[key], previous: getPercentageDiff(previousValue, currentMetrics[key], key === 'durationSeconds' || key === 'manHoursSeconds'
            ? { comparisonValueFormat: diffComparisonValueFormat.durationSeconds }
            : undefined) }, (teamValue !== undefined && {
        teamAverage: getPercentageDiff(teamValue, currentMetrics[key], key === 'durationSeconds' || key === 'manHoursSeconds'
            ? { comparisonValueFormat: diffComparisonValueFormat.durationSeconds }
            : undefined)
    }))); };
    return {
        periods: periodsData,
        totals: __assign({ meetingCount: buildMetricTotal('meetingCount', previousMetrics.meetingCount, teamMetrics === null || teamMetrics === void 0 ? void 0 : teamMetrics.meetingCount), durationSeconds: buildMetricTotal('durationSeconds', previousMetrics.durationSeconds, teamMetrics === null || teamMetrics === void 0 ? void 0 : teamMetrics.durationSeconds) }, (includeTeamMetrics && {
            manHoursSeconds: buildMetricTotal('manHoursSeconds', previousMetrics.manHoursSeconds, teamMetrics === null || teamMetrics === void 0 ? void 0 : teamMetrics.manHoursSeconds),
            avgParticipants: buildMetricTotal('avgParticipants', previousMetrics.avgParticipants, teamMetrics === null || teamMetrics === void 0 ? void 0 : teamMetrics.avgParticipants)
        }))
    };
};
